import { useEffect, useState } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { XCircle, AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'

export function ConsentErrorPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as { message?: string }
  const [countdown, setCountdown] = useState(15)

  const errorMessage = search.message || 'Unknown error occurred'

  useEffect(() => {
    // Auto-redirect after 15 seconds
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          navigate({ to: '/admin/tenants' })
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [navigate])

  const handleGoToTenants = () => {
    navigate({ to: '/admin/tenants' })
  }

  const handleTryAgain = () => {
    navigate({ to: '/admin/tenants' })
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-orange-50 dark:from-gray-900 dark:to-gray-800 p-4">
      <Card className="max-w-2xl w-full shadow-xl">
        <CardHeader className="text-center pb-6">
          <div className="flex justify-center mb-4">
            <div className="rounded-full bg-red-100 dark:bg-red-900 p-6">
              <XCircle className="h-16 w-16 text-red-600 dark:text-red-400" />
            </div>
          </div>
          <CardTitle className="text-3xl font-bold text-red-900 dark:text-red-100">
            {t('admin.consent.errorTitle', 'Admin Consent Failed')}
          </CardTitle>
          <CardDescription className="text-lg mt-2">
            {t(
              'admin.consent.errorDescription',
              'There was a problem granting admin consent. Please try again.'
            )}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>{t('admin.consent.errorDetails', 'Error Details')}</AlertTitle>
            <AlertDescription className="mt-2 font-mono text-xs">
              {errorMessage}
            </AlertDescription>
          </Alert>

          <div className="bg-orange-50 dark:bg-orange-950 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
            <h3 className="font-semibold text-orange-900 dark:text-orange-100 mb-2">
              {t('admin.consent.troubleshooting', 'Troubleshooting Steps')}
            </h3>
            <ul className="space-y-2 text-sm text-orange-800 dark:text-orange-200">
              <li className="flex items-start">
                <span className="mr-2">1.</span>
                <span>
                  {t(
                    'admin.consent.troubleshootStep1',
                    'Verify that the tenant is registered in the whitelist'
                  )}
                </span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">2.</span>
                <span>
                  {t(
                    'admin.consent.troubleshootStep2',
                    'Ensure you have Global Administrator or Application Administrator privileges in Azure AD'
                  )}
                </span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">3.</span>
                <span>
                  {t(
                    'admin.consent.troubleshootStep3',
                    'Check that the Azure AD app registration has the correct redirect URIs configured'
                  )}
                </span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">4.</span>
                <span>
                  {t(
                    'admin.consent.troubleshootStep4',
                    'If the error persists, contact your system administrator'
                  )}
                </span>
              </li>
            </ul>
          </div>

          <div className="text-center text-sm text-muted-foreground">
            {t(
              'admin.consent.autoRedirect',
              'You will be automatically redirected to the Tenant Management page in {{seconds}} seconds...',
              { seconds: countdown }
            )}
          </div>

          <div className="flex gap-3">
            <Button onClick={handleTryAgain} variant="default" className="flex-1" size="lg">
              {t('admin.consent.tryAgain', 'Try Again')}
            </Button>
            <Button onClick={handleGoToTenants} variant="outline" className="flex-1" size="lg">
              {t('admin.consent.goBack', 'Go Back')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
